import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

import { Text } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useWorkOrderStats } from '@/hooks/use-work-order-stats';
import { UserRole } from '@/types/models';

/**
 * Open work orders BY PROPERTY (#279).
 *
 * ── WHY PROPERTY AND NOT STATUS ──
 *
 * A status ring told you the shape of the queue, which nobody acts on: the statuses are
 * already the sidebar's navigation. "Which of my communities is generating the work" is the
 * question a property manager actually has, and it points at somewhere to go.
 *
 * ── OPEN ONLY, SO THE RING MATCHES THE TILE ──
 *
 * Counts exactly what the "Open Work Orders" tile counts, so the centre total and the tile
 * are always one number. A ring over all-time history would be dominated by completed work
 * and answer nothing about today.
 *
 * ── THE TAIL IS ROLLED UP, NOT TRUNCATED ──
 *
 * A portfolio of sixty associations is a legend nobody reads. Everything past the top few
 * becomes a single "Other" slice, so the arcs still sum to the total — dropping the tail
 * instead would leave a visible gap in the ring and a centre number that did not add up.
 *
 * ── ONE SVG CIRCLE PER SLICE, NOT A CHART LIBRARY ──
 *
 * `react-native-svg` is already a dependency; victory/recharts are not, and neither is
 * worth several hundred KB for one ring. Each slice is a `Circle` whose `strokeDasharray`
 * is "arc length, remainder", walked around the track by `strokeDashoffset`.
 */

/** How many properties get their own slice before the rest roll into "Other". */
const MAX_SLICES = 6;

/**
 * Slice colours, in order.
 *
 * A fixed ramp rather than a hash of the association id: a stable, readable sequence beats
 * per-name colours that can land two near-identical blues next to each other.
 */
const PALETTE = ['#1976d2', '#26a69a', '#ffa726', '#ab47bc', '#42a5f5', '#66bb6a'];
const OTHER_COLOR = '#b0bec5';

const SIZE = 168;
const STROKE = 26;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export const WorkOrderOverview: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const { user } = useApi();
	const pmScope = user?.role === UserRole.PropertyManager ? user?.id : undefined;
	const { data: stats, isLoading } = useWorkOrderStats(pmScope);

	const { arcs, total } = useMemo(() => {
		const rows = stats?.byAssociation ?? [];
		const grandTotal = rows.reduce((sum, r) => sum + r.count, 0);

		const head = rows.slice(0, MAX_SLICES).map((r, i) => ({
			key: r.associationId ?? `unassigned-${i}`,
			label: r.name,
			count: r.count,
			color: PALETTE[i % PALETTE.length],
			associationId: r.associationId,
		}));
		const tail = rows.slice(MAX_SLICES);
		if (tail.length > 0) {
			head.push({
				key: 'other',
				label: `${tail.length} other propert${tail.length === 1 ? 'y' : 'ies'}`,
				count: tail.reduce((sum, r) => sum + r.count, 0),
				color: OTHER_COLOR,
				associationId: null,
			});
		}

		// Cumulative offsets, walking the ring clockwise from 12 o'clock.
		let walked = 0;
		const withArcs = head.map((s) => {
			const fraction = grandTotal > 0 ? s.count / grandTotal : 0;
			const arc = { ...s, fraction, offset: walked };
			walked += fraction;
			return arc;
		});
		return { arcs: withArcs, total: grandTotal };
	}, [stats]);

	return (
		<View style={[styles.card, style]} testID="v2-work-order-overview">
			<View style={styles.header}>
				<Ionicons name="business-outline" size={18} color="#546e7a" />
				<Text style={styles.title}>Open Work Orders by Property</Text>
				<Pressable onPress={() => router.push('/work-orders')} testID="v2-overview-view-all">
					<Text style={styles.link}>View all →</Text>
				</Pressable>
			</View>

			{isLoading || total === 0 ? (
				<Text style={styles.empty}>
					{isLoading ? 'Loading…' : 'No open work orders.'}
				</Text>
			) : (
				<View style={styles.body}>
					<View style={styles.chartWrap}>
						<Svg width={SIZE} height={SIZE}>
							{/* Track, so a single-slice ring still reads as a ring. */}
							<Circle
								cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
								stroke="#eceff1" strokeWidth={STROKE} fill="none"
							/>
							{arcs.map((a) => (
								<Circle
									key={a.key}
									cx={SIZE / 2}
									cy={SIZE / 2}
									r={RADIUS}
									stroke={a.color}
									strokeWidth={STROKE}
									fill="none"
									strokeDasharray={`${a.fraction * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
									strokeDashoffset={-a.offset * CIRCUMFERENCE}
									// SVG's zero angle is at 3 o'clock; rings read from 12.
									transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
									strokeLinecap="butt"
								/>
							))}
						</Svg>
						<View style={styles.centerLabel} pointerEvents="none">
							<Text style={styles.centerValue}>{total}</Text>
							<Text style={styles.centerCaption}>Open</Text>
						</View>
					</View>

					<View style={styles.legend}>
						{arcs.map((a) => (
							<Pressable
								key={a.key}
								// "Other" has no single destination, so it is not a link.
								onPress={a.associationId
									? () => router.push(`/associations/${a.associationId}` as never)
									: undefined}
								style={styles.legendRow}
								testID={`v2-legend-${a.key}`}
							>
								<View style={[styles.dot, { backgroundColor: a.color }]} />
								<Text numberOfLines={1} style={styles.legendLabel}>{a.label}</Text>
								<Text style={styles.legendCount}>{a.count}</Text>
								{/*
									Rounded for display only — the ARCS use exact fractions, so the
									ring always closes even when the labels read 99% or 101%. The
									count beside it is the number to trust.
								*/}
								<Text style={styles.legendPct}>{Math.round(a.fraction * 100)}%</Text>
							</Pressable>
						))}
					</View>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: '#fff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e8eaed',
		padding: 18,
	},
	header: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
	title: { flex: 1, minWidth: 0, fontSize: 16, fontWeight: '700', color: '#263238' },
	link: { color: '#1976d2', fontSize: 13, fontWeight: '500' },
	body: { flexDirection: 'row', alignItems: 'center', gap: 24, flexWrap: 'wrap' },
	chartWrap: { width: SIZE, height: SIZE, alignItems: 'center', justifyContent: 'center' },
	centerLabel: { position: 'absolute', alignItems: 'center' },
	centerValue: { fontSize: 30, fontWeight: '700', color: '#1a2027' },
	centerCaption: { fontSize: 12, color: '#90a4ae' },
	// minWidth 0 so a long association name truncates instead of forcing the card wider —
	// RN-Web flex children default to min-width:auto.
	legend: { flex: 1, minWidth: 200, gap: 2 },
	legendRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 5 },
	dot: { width: 10, height: 10, borderRadius: 5 },
	legendLabel: { flex: 1, minWidth: 0, fontSize: 13, color: '#37474f' },
	legendCount: { fontSize: 14, fontWeight: '600', color: '#263238', minWidth: 30, textAlign: 'right' },
	legendPct: { fontSize: 13, color: '#90a4ae', minWidth: 40, textAlign: 'right' },
	empty: { color: '#90a4ae', fontStyle: 'italic', paddingVertical: 28, textAlign: 'center' },
});
